import { useInfiniteQuery } from "@tanstack/react-query";
import { fetchClient } from "@/lib/api-client";

const PAGE_SIZE = 10;

/**
 * Infinite-scroll listing of projects for the fast-checkout "pick a project"
 * tab. `GET /project` is fully paginated server-side; this consumes it
 * page-by-page instead of hardcoding `page: 1` and silently stranding
 * everything past page 1 (same pattern as `useFetchProducts`,
 * `useLaunchpadListings`, etc.).
 *
 * Kept separate from `useFetchProjects` on purpose: that hook backs the
 * homepage's curated, bounded "featured projects" preview via
 * `useHomeProjects` and must not become an infinite list.
 */
export const useFetchProjectsInfinite = () =>
  useInfiniteQuery({
    queryKey: ["get", "/project", "infinite"],
    queryFn: async ({ pageParam }) => {
      const response = await fetchClient.GET("/project", {
        params: { query: { page: pageParam, limit: PAGE_SIZE } },
      });
      const error = response.error;
      if (error) throw error;
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.data?.pagination;
      if (pagination?.hasNextPage) return pagination.page + 1;
      return undefined;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
